import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Send, RotateCcw, Minimize2 } from "lucide-react";
import { useChat } from "./ChatContext";
import ReactMarkdown from 'react-markdown';

// Add some custom styles for markdown inside the chat
const markdownStyles = `
  .chat-markdown p { margin-bottom: 0.5em; }
  .chat-markdown p:last-child { margin-bottom: 0; }
  .chat-markdown a { color: #22d3ee; text-decoration: underline; text-underline-offset: 2px; }
  .chat-markdown a:hover { color: #a78bfa; }
  .chat-markdown ul { list-style-type: disc; padding-left: 1.5em; margin-bottom: 0.5em; }
  .chat-markdown strong { font-weight: 700; color: #fff; }
`;

const CHAT_API_URL = `${process.env.REACT_APP_API_URL || "/api"}/mistral/`;

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

const getIntroMessage = (language: string): Message => ({
  id: "intro",
  role: "assistant",
  content: language === "en"
    ? "Hi! 👋 I'm Sami's AI version. I know everything about my background, projects, and skills. Ask me anything!"
    : "Salut ! 👋 Je suis Sami en version IA. Je connais tout de mon parcours, mes projets et mes compétences. Pose-moi ce que tu veux !",
});

const getSuggestions = (language: string) => language === "en" ? [
  "What is your favorite stack?",
  "Are you available for a mission?",
  "Tell me about your VSCode project",
  "What are your certifications?",
] : [
  "C'est quoi ta stack préférée ?",
  "Tu es dispo pour une mission ?",
  "Parle-moi de ton projet VSCode",
  "Quelles sont tes certifications ?",
];

function TypingIndicator({ color }: { color: string }) {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: color, opacity: 0.7 }}
        />
      ))}
    </div>
  );
}

function Avatar({ size = 40, pulse = false }: { size?: number; pulse?: boolean }) {
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      {pulse && (
        <motion.div
          animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 rounded-full"
          style={{ background: "rgba(34,211,238,0.4)" }}
        />
      )}
      <div
        className="relative rounded-full flex items-center justify-center overflow-hidden"
        style={{
          width: size,
          height: size,
          background: "linear-gradient(135deg, #22d3ee20, #a78bfa30)",
          border: "2px solid rgba(34,211,238,0.4)",
          boxShadow: "0 0 16px rgba(34,211,238,0.2)",
        }}
      >
        {/* Robot face SVG */}
        <svg width={size * 0.65} height={size * 0.65} viewBox="0 0 32 32" fill="none">
          {/* Head */}
          <rect x="6" y="8" width="20" height="16" rx="4" fill="rgba(34,211,238,0.15)" stroke="#22d3ee" strokeWidth="1.5" />
          {/* Antenna */}
          <line x1="16" y1="8" x2="16" y2="4" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="16" cy="3" r="1.5" fill="#22d3ee" />
          {/* Eyes */}
          <rect x="10" y="13" width="4" height="3" rx="1" fill="#22d3ee" />
          <rect x="18" y="13" width="4" height="3" rx="1" fill="#22d3ee" />
          {/* Mouth */}
          <path d="M12 20 Q16 23 20 20" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          {/* Ears */}
          <rect x="3" y="13" width="3" height="5" rx="1.5" fill="rgba(34,211,238,0.4)" stroke="#22d3ee" strokeWidth="1" />
          <rect x="26" y="13" width="3" height="5" rx="1.5" fill="rgba(34,211,238,0.4)" stroke="#22d3ee" strokeWidth="1" />
        </svg>
      </div>
    </div>
  );
}

export function FloatingChat({ language = "fr" }: { language?: string }) {
  const [phase, setPhase] = useState<"hidden" | "bubble" | "open">("hidden");
  const [messages, setMessages] = useState<Message[]>([getIntroMessage(language)]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { registerSendMessage, registerOpenChat } = useChat();

  // Show bubble after 5s
  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => setPhase("bubble"), 5000);
    return () => clearTimeout(t);
  }, [dismissed]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when chat opens
  useEffect(() => {
    if (phase === "open") {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [phase]);

  // Register with ChatContext so external components can trigger messages
  useEffect(() => {
    registerOpenChat(() => {
      setDismissed(false);
      setPhase("open");
    });
  }, [registerOpenChat]);

  const sendMessage = useCallback(async (text?: string, context?: string) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");

    const userMsg: Message = { id: Date.now().toString(), role: "user", content };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    const botMsgId = (Date.now() + 1).toString();
    setMessages((prev) => [
      ...prev,
      { id: botMsgId, role: "assistant", content: "" },
    ]);

    try {
      const body: any = { prompt: content, language };
      if (context) body.context = context;
      const res = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("API error");

      const reader = res.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const dataStr = line.replace("data: ", "").trim();
              if (!dataStr) continue;

              try {
                const data = JSON.parse(dataStr);

                if (data.error) {
                  throw new Error(data.error);
                }

                if (data.content) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMsgId
                        ? { ...msg, content: msg.content + data.content }
                        : msg
                    )
                  );
                }
              } catch (e) {
                console.error("Error parsing SSE chunk:", e);
              }
            }
          }
        }
      }
    } catch {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMsgId
            ? {
              ...msg, content: language === "en"
                ? "Oops, my backend is unreachable right now. In the meantime, explore my CV!"
                : "Oups, mon backend est injoignable pour l'instant. En attendant, explore mon CV !"
            }
            : msg
        )
      );
    } finally {
      setLoading(false);
    }
  }, [input, loading, language]);

  // Register sendMessage with ChatContext
  useEffect(() => {
    registerSendMessage(sendMessage as (text: string) => Promise<void>);
  }, [sendMessage, registerSendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([getIntroMessage(language)]);
    setInput("");
  };

  const dismiss = () => {
    setDismissed(true);
    setPhase("hidden");
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[990] flex flex-col items-end gap-3"
      style={{ cursor: "none" }}
    >
      <style>{markdownStyles}</style>
      {/* ── Chat window ── */}
      <AnimatePresence>
        {phase === "open" && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.92, transformOrigin: "bottom right" }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.92 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col rounded-2xl overflow-hidden"
            style={{
              width: "min(380px, calc(100vw - 48px))",
              height: "520px",
              background: "#09090d",
              border: "1px solid rgba(34,211,238,0.18)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.6), 0 0 40px rgba(34,211,238,0.06)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.025)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Avatar size={34} />
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#ffffff",
                    lineHeight: 1.3,
                  }}
                >
                  {language === "en" ? "Sami · AI" : "Sami · IA"}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 4px #34d399" }} />
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "10px",
                      color: "rgba(255,255,255,0.3)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {language === "en" ? "ONLINE" : "EN LIGNE"}
                  </span>
                </div>
              </div>
              <button
                onClick={resetChat}
                className="p-1.5 rounded-lg transition-colors"
                title={language === "en" ? "Reset" : "Réinitialiser"}
                style={{ color: "rgba(255,255,255,0.25)", cursor: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.25)")}
              >
                <RotateCcw size={14} />
              </button>
              <button
                onClick={() => setPhase("bubble")}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: "rgba(255,255,255,0.25)", cursor: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.25)")}
              >
                <Minimize2 size={14} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(34,211,238,0.15) transparent" }}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                >
                  {msg.role === "assistant" && <Avatar size={28} />}
                  <div
                    className="max-w-[78%] px-3.5 py-2.5 rounded-2xl"
                    style={{
                      background:
                        msg.role === "user"
                          ? "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(167,139,250,0.2))"
                          : "rgba(255,255,255,0.05)",
                      border:
                        msg.role === "user"
                          ? "1px solid rgba(34,211,238,0.25)"
                          : "1px solid rgba(255,255,255,0.06)",
                      borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "14px",
                      color: msg.role === "user" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.75)",
                      lineHeight: 1.65,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <ReactMarkdown className="chat-markdown">{msg.content}</ReactMarkdown>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex gap-3 items-start">
                  <Avatar size={28} />
                  <div
                    className="rounded-2xl"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: "18px 18px 18px 4px",
                    }}
                  >
                    <TypingIndicator color="#22d3ee" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions (show only if few messages) */}
            {messages.length <= 2 && (
              <div className="flex-shrink-0 px-4 pb-2 flex flex-wrap gap-2">
                {getSuggestions(language).map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="px-3 py-1.5 rounded-full transition-all text-left"
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.5)",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      cursor: "none",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#22d3ee";
                      e.currentTarget.style.borderColor = "rgba(34,211,238,0.35)";
                      e.currentTarget.style.background = "rgba(34,211,238,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div
              className="flex-shrink-0 flex items-center gap-3 px-4 py-3"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pose-moi une question…"
                disabled={loading}
                className="flex-1 bg-transparent outline-none"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.85)",
                  cursor: "none",
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="flex items-center justify-center w-8 h-8 rounded-full transition-all flex-shrink-0"
                style={{
                  background:
                    input.trim() && !loading
                      ? "linear-gradient(135deg, #22d3ee, #a78bfa)"
                      : "rgba(255,255,255,0.07)",
                  color: input.trim() && !loading ? "#050508" : "rgba(255,255,255,0.2)",
                  cursor: "none",
                  transition: "all 0.2s",
                  boxShadow: input.trim() && !loading ? "0 0 12px rgba(34,211,238,0.4)" : "none",
                }}
              >
                <Send size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bubble phase ── */}
      <AnimatePresence>
        {phase === "bubble" && (
          <motion.div
            initial={{ opacity: 0, x: 20, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 10, y: 6, scale: 0.9 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-end gap-3"
          >
            {/* Speech bubble */}
            <div className="flex flex-col items-end gap-2">
              <div
                className="relative px-4 py-3 rounded-2xl max-w-[220px]"
                style={{
                  background: "#0d1117",
                  border: "1px solid rgba(34,211,238,0.25)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(34,211,238,0.08)",
                }}
              >
                {/* Tail */}
                <div
                  className="absolute -bottom-2 right-6"
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: "8px solid transparent",
                    borderRight: "8px solid transparent",
                    borderTop: "8px solid rgba(34,211,238,0.25)",
                  }}
                />
                <div
                  className="absolute -bottom-1.5 right-6 ml-px"
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: "7px solid transparent",
                    borderRight: "7px solid transparent",
                    borderTop: "7px solid #0d1117",
                  }}
                />
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.85)",
                    lineHeight: 1.5,
                  }}
                >
                  {language === "en" ? "Hey, it's me! 👋" : "Hey c'est moi ! 👋"}<br />
                  <span style={{ color: "#22d3ee" }}>{language === "en" ? "Wanna chat?" : "Tu veux me parler ?"}</span>
                </p>
              </div>

              {/* Dismiss */}
              <button
                onClick={dismiss}
                className="text-xs"
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.2)",
                  cursor: "none",
                  letterSpacing: "0.06em",
                  background: "none",
                  border: "none",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.2)")}
              >
                {language === "en" ? "dismiss ×" : "ignorer ×"}
              </button>
            </div>

            {/* Avatar button */}
            <motion.button
              onClick={() => setPhase("open")}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              style={{ background: "none", border: "none", cursor: "none", padding: 0 }}
            >
              <Avatar size={52} pulse />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Collapsed FAB (when open or after bubble) ── */}
      <AnimatePresence>
        {phase === "open" && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setPhase("bubble")}
            className="flex items-center justify-center w-12 h-12 rounded-full"
            style={{
              background: "linear-gradient(135deg, #22d3ee, #a78bfa)",
              boxShadow: "0 0 24px rgba(34,211,238,0.35)",
              cursor: "none",
              border: "none",
              color: "#050508",
            }}
          >
            <X size={18} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
